import {
  getSubscriptionRetryDelayMs,
  keepIdentityWhenUnchanged,
  MAX_SUBSCRIPTION_RETRIES,
} from '@griply/utils';
import { hashKey, QueryFunction } from '@tanstack/react-query';
import {
  DocumentReference,
  DocumentSnapshot,
  FirestoreError,
  FirestoreErrorCode,
  getDocFromCache,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import { queryClient } from './query-client';
import {
  registerSubscription,
  unregisterSubscription,
} from './subscription-registry';

export type CreateSubscriptionDocOptions = {
  // Expected answers for this doc (permission-denied on a rules-guarded doc that does not exist) reject without a Sentry event.
  expectedErrorCodes?: FirestoreErrorCode[];
};

export const createSubscriptionDoc = <T extends { id: ID }>(
  doc: () => Promise<DocumentReference<T>> | DocumentReference<T>,
  { expectedErrorCodes = [] }: CreateSubscriptionDocOptions = {},
): QueryFunction<T | undefined, string[]> => {
  return (context) => {
    getErrorReporter().addBreadcrumb({
      category: 'tanstack-query',
      message: JSON.stringify(context.queryKey),
    });

    // per-invocation: a closure-level firstRun never settles TanStack retry promises
    let firstRun = true;
    let retryCount = 0;

    return new Promise(async (resolve, reject) => {
      const docRef = await doc();
      const queryHash = hashKey(context.queryKey);

      let unsubscribe: Unsubscribe | undefined;
      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) {
          return;
        }
        cleanedUp = true;
        unsubscribe?.();
      };

      const onSuccess = (response: DocumentSnapshot<T>) => {
        if (!response.metadata.fromCache) {
          retryCount = 0;
        }

        const existing = queryClient.getQueryData<T>(context.queryKey);
        const next = keepIdentityWhenUnchanged(
          existing,
          (response.data() as T) ?? null,
        );

        if (firstRun) {
          resolve(next);
          firstRun = false;
          return;
        }

        if (next === existing) {
          return;
        }
        queryClient.setQueryData(context.queryKey, next);
      };

      const giveUp = (error: FirestoreError) => {
        const expected = expectedErrorCodes.includes(error.code);
        if (!expected) {
          getErrorReporter().captureException(error, {
            tags: {
              source: 'firestore-subscription-doc',
              queryKey: JSON.stringify(context.queryKey),
            },
            contexts: { subscriptionDoc: { queryKey: context.queryKey } },
          });
        }

        unregisterSubscription(queryHash, cleanup);
        cleanup();

        if (firstRun) {
          reject(error);
          firstRun = false;
          return;
        }

        // an expected answer means the doc is gone: consumers key their recovery on `null` data
        if (expected) {
          queryClient.setQueryData(context.queryKey, null);
          return;
        }

        // not invalidateQueries: it re-runs the queryFn with a fresh counter and re-subscribes without bound
        const cachedQuery = queryClient.getQueryCache().get(queryHash);
        cachedQuery?.setState({ status: 'error', error, fetchStatus: 'idle' });
      };

      const onError = (error: FirestoreError) => {
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- non-prod logging
          console.warn(
            `[Debug-Subscription] ERROR doc ${JSON.stringify(context.queryKey)} retry=${retryCount} at ${performance.now().toFixed(0)}ms`,
            error,
          );
        }
        unsubscribe?.();
        unsubscribe = undefined;

        // an expected answer is final; retrying it only delays the caller's fallback
        if (
          expectedErrorCodes.includes(error.code) ||
          retryCount >= MAX_SUBSCRIPTION_RETRIES
        ) {
          giveUp(error);
          return;
        }

        retryCount += 1;
        setTimeout(() => {
          if (cleanedUp || context.signal?.aborted) {
            return;
          }
          subscribe();
        }, getSubscriptionRetryDelayMs(retryCount));
      };

      const subscribe = () => {
        unsubscribe = onSnapshot(docRef, onSuccess, onError);
      };

      getDocFromCache(docRef)
        .then(onSuccess)
        .catch(() => {
          // this is just cache, do nothing with this error
        });

      subscribe();
      registerSubscription(queryHash, cleanup);

      context.signal?.addEventListener('abort', () => {
        unregisterSubscription(queryHash, cleanup);
        cleanup();
        if (firstRun) {
          firstRun = false;
          reject(new Error('Query aborted by React Query'));
        }
      });
    });
  };
};
